/**
 * DutyStatusCard
 * Shows a pilot's duty time status with rolling window progress bars and alerts.
 * Used on both pilot dashboard (own status) and admin duty time page.
 */

import type { PilotDutyStatus, WindowStatus } from '@/services/dutyTimeService';
import {
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';

interface DutyStatusCardProps {
  status: PilotDutyStatus;
  showPilotName?: boolean;
  compact?: boolean;
}

function WindowBar({ label, window }: { label: string; window: WindowStatus }) {
  const barColor =
    window.percent >= 100
      ? 'bg-danger'
      : window.percent >= 90
        ? 'bg-warning'
        : window.percent >= 80
          ? 'bg-amber-400'
          : 'bg-primary';

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium text-foreground">
          {window.used}h / {window.limit}h
        </span>
      </div>
      <div className="w-full bg-muted rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${Math.min(window.percent, 100)}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">
        {window.remaining}h remaining
      </p>
    </div>
  );
}

export default function DutyStatusCard({ status, showPilotName = false, compact = false }: DutyStatusCardProps) {
  const statusColor = {
    green: 'border-success/30',
    amber: 'border-warning/30',
    red: 'border-danger/30',
  }[status.overallStatus];

  const statusBg = {
    green: 'bg-success/5',
    amber: 'bg-warning/5',
    red: 'bg-danger/5',
  }[status.overallStatus];

  const StatusIcon = status.overallStatus === 'green'
    ? CheckCircleIcon
    : status.overallStatus === 'amber'
      ? ExclamationTriangleIcon
      : ShieldExclamationIcon;

  const statusIconColor = {
    green: 'text-success',
    amber: 'text-warning',
    red: 'text-danger',
  }[status.overallStatus];

  return (
    <div className={`rounded-lg border ${statusColor} ${statusBg} overflow-hidden`}>
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-border/50">
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-5 h-5 ${statusIconColor}`} />
          <div>
            {showPilotName && (
              <p className="font-semibold text-foreground text-sm">{status.pilotName}</p>
            )}
            <p className={`text-muted-foreground ${showPilotName ? 'text-xs' : 'text-sm font-medium'}`}>
              {showPilotName ? (status.homeBase || 'No home base') : 'Duty Time Status'}
            </p>
          </div>
        </div>
        <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
          status.overallStatus === 'green'
            ? 'bg-success/10 text-success'
            : status.overallStatus === 'amber'
              ? 'bg-warning/10 text-warning'
              : 'bg-danger/10 text-danger'
        }`}>
          {status.overallStatus}
        </span>
      </div>

      <div className="p-4 space-y-4">
        {/* Current FDP */}
        {status.currentFDP.active && (
          <div className="flex items-center gap-3 p-3 rounded-md bg-card border border-border">
            <ClockIcon className="w-5 h-5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">On Duty</p>
              <p className="text-xs text-muted-foreground">
                {status.currentFDP.elapsedHours}h elapsed / {status.currentFDP.limit}h max
                {' '}({status.currentFDP.remaining}h remaining)
              </p>
            </div>
          </div>
        )}

        {/* Rolling Windows */}
        <div className={compact ? 'space-y-2' : 'space-y-3'}>
          <WindowBar label="7 Day" window={status.windows.hours7day} />
          <WindowBar label="30 Day" window={status.windows.hours30day} />
          {!compact && (
            <>
              <WindowBar label="90 Day" window={status.windows.hours90day} />
              <WindowBar label="365 Day" window={status.windows.hours365day} />
            </>
          )}
        </div>

        {/* Last Rest */}
        {!compact && status.lastRest.durationHours !== null && (
          <div className={`flex items-center gap-2 text-sm p-2 rounded-md ${
            status.lastRest.meetsMinimum
              ? 'bg-success/5 text-success'
              : 'bg-danger/5 text-danger'
          }`}>
            {status.lastRest.meetsMinimum ? (
              <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
            ) : (
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
            )}
            <span>
              Last rest: {status.lastRest.durationHours}h
              {status.lastRest.location && ` at ${status.lastRest.location}`}
              {status.lastRest.wasAtHomeBase && ' (home base)'}
              {' '}&mdash; min {status.lastRest.minimumRequired}h required
            </span>
          </div>
        )}

        {/* Alerts */}
        {status.alerts.length > 0 && (
          <div className="space-y-1">
            {status.alerts.slice(0, compact ? 2 : undefined).map((alert, i) => (
              <div
                key={i}
                className={`flex items-start gap-2 text-xs p-2 rounded-md ${
                  alert.type === 'exceeded'
                    ? 'bg-danger/10 text-danger'
                    : alert.type === 'critical'
                      ? 'bg-warning/10 text-warning'
                      : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                }`}
              >
                <ExclamationTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{alert.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
